import React, { useState } from 'react';
import { 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  Image, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  Instagram, 
  MapPin,
  Calendar,
  DollarSign,
  Info,
  ArrowLeft
} from 'lucide-react';
import { getCloudinaryUrl } from '../../utils/imageUtils';
import '../../styles/SubmissionSystem.css';

const ReviewSubmit = ({ submissionData, formData, files, onSubmit, onPrevious, saving, error, onTermsAgreement, feeWaiverStatus }) => {
  const [showPaymentInfo, setShowPaymentInfo] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(formData.agreeToTerms || false);
  const [artworksExpanded, setArtworksExpanded] = useState(false);

  // Update form data when terms agreement changes
  const handleTermsChange = (e) => {
    const checked = e.target.checked;
    setAgreeToTerms(checked);
    // Update the parent component's form data
    if (onTermsAgreement) {
      onTermsAgreement(checked);
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Deadline passed';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    if (days <= 7) return `Due in ${days} days`;
    
    return new Date(deadline).toLocaleDateString();
  };

  const getDeadlineColor = (deadline) => {
    if (!deadline) return 'text-gray-500';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'text-red-500';
    if (days <= 3) return 'text-red-600';
    if (days <= 7) return 'text-orange-600';
    if (days <= 14) return 'text-yellow-600';
    
    return 'text-green-600';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="review-submit-content">
      {/* Header */}
      <div className="review-header">
        <h2 className="review-title">Review & Submit</h2>
        <p className="review-subtitle">
          Review your application details before final submission
        </p>
      </div>

      <div className="review-sections">
        {/* Artist Statement */}
        {submissionData.form_config?.sections?.artist_statement?.enabled && formData.artist_statement && (
          <div className="review-section">
            <h3 className="review-section-title">
              {submissionData.form_config.sections.artist_statement.label || 'Artist Statement'}
            </h3>
            <div className="statement-preview">
              <p className="statement-text">{formData.artist_statement}</p>
              <div className="statement-meta">
                <span className="character-count">
                  {formData.artist_statement.length}/{submissionData.form_config.sections.artist_statement.max_length || 200} characters
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Professional Information */}
        {submissionData.form_config?.sections?.professional_info?.enabled && 
         (formData.preferred_mediums?.length > 0 || formData.years_experience || formData.education) && (
          <div className="review-section">
            <h3 className="review-section-title">Professional Information</h3>
            <div className="professional-info">
              {submissionData.form_config.sections.professional_info.fields?.preferred_mediums?.enabled && 
               formData.preferred_mediums?.length > 0 && (
                <div className="info-item">
                  <span className="info-label">Preferred Mediums:</span>
                  <span className="info-value">{formData.preferred_mediums.join(', ')}</span>
                </div>
              )}
              {submissionData.form_config.sections.professional_info.fields?.years_experience?.enabled && 
               formData.years_experience && (
                <div className="info-item">
                  <span className="info-label">Years of Experience:</span>
                  <span className="info-value">{formData.years_experience}</span>
                </div>
              )}
              {submissionData.form_config.sections.professional_info.fields?.education?.enabled && 
               formData.education && (
                <div className="info-item">
                  <span className="info-label">Education:</span>
                  <span className="info-value">{formData.education}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Awards & Exhibitions */}
        {(submissionData.form_config?.sections?.awards?.enabled && formData.awards?.length > 0) || 
         (submissionData.form_config?.sections?.exhibitions?.enabled && formData.exhibitions?.length > 0) ? (
          <div className="review-section">
            <h3 className="review-section-title">Awards & Exhibitions</h3>
            {submissionData.form_config?.sections?.awards?.enabled && formData.awards?.length > 0 && (
              <div className="awards-exhibitions">
                <h4 className="subsection-title">
                  {submissionData.form_config.sections.awards.label || 'Awards & Recognition'}
                </h4>
                <div className="awards-list">
                  {formData.awards.map((award, index) => (
                    <div key={index} className="award-item">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>{award}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {submissionData.form_config?.sections?.exhibitions?.enabled && formData.exhibitions?.length > 0 && (
              <div className="awards-exhibitions">
                <h4 className="subsection-title">
                  {submissionData.form_config.sections.exhibitions.label || 'Notable Exhibitions'}
                </h4>
                <div className="exhibitions-list">
                  {formData.exhibitions.map((exhibition, index) => (
                    <div key={index} className="exhibition-item">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>{exhibition}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
        
        {/* Submitted Artworks */}
        <div className="review-section">
          <div className="artwork-section-header" onClick={() => setArtworksExpanded(!artworksExpanded)}>
            <h3 className="review-section-title">Submitted Artworks ({files.length})</h3>
            <button type="button" className="expand-toggle">
              {artworksExpanded ? '−' : '+'}
            </button>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="error-message" style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              color: '#dc2626', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '1rem' 
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
          
          {files.length > 0 ? (
            artworksExpanded ? (
              // Expanded view - full details
              <div className="artwork-preview-grid">
                {files.map((file, index) => (
                  <div key={index} className="artwork-preview-card">
                    <div className="artwork-image">
                      {file.public_id ? (
                        <img 
                          src={getCloudinaryUrl(file.public_id, { width: 200, height: 200 })} 
                          alt={file.artwork_title || `Artwork ${index + 1}`} 
                          className="artwork-preview-image"
                        />
                      ) : file.file_url ? (
                        <img 
                          src={file.file_url} 
                          alt={file.artwork_title || `Artwork ${index + 1}`} 
                          className="artwork-preview-image"
                        />
                      ) : file.preview ? (
                        <img 
                          src={file.preview} 
                          alt={file.artwork_title || `Artwork ${index + 1}`} 
                          className="artwork-preview-image"
                        />
                      ) : (
                        <div className="artwork-placeholder">
                          <Image className="w-12 h-12 text-gray-400" />
                          <span className="text-sm text-gray-500">No preview</span>
                        </div>
                      )}
                    </div>
                    <div className="artwork-info">
                      <h4 className="artwork-title">{file.artwork_title || `Untitled ${index + 1}`}</h4>
                      {file.medium && <p className="artwork-medium">{file.medium}</p>}
                      {file.width && file.height && (
                        <p className="artwork-dimensions">
                          {file.width} × {file.height} {file.unit || 'inches'}
                        </p>
                      )}
                      {file.year_created && <p className="artwork-year">{file.year_created}</p>}
                      {file.price && (
                        <p className="artwork-price">${file.price}</p>
                      )}
                      {file.description && (
                        <p className="artwork-description">{file.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Condensed view - compact display
              <div className="artwork-condensed-list">
                {files.map((file, index) => (
                  <div key={index} className="artwork-card-condensed">
                    <img 
                      src={file.public_id ? getCloudinaryUrl(file.public_id, { width: 60, height: 60 }) : (file.file_url || file.preview)} 
                      alt={file.artwork_title || `Artwork ${index + 1}`}
                      className="condensed-image"
                    />
                    
                    <div className="condensed-info">
                      <div className="condensed-title">
                        {file.artwork_title || `Untitled Artwork ${index + 1}`}
                      </div>
                      <div className="condensed-details">
                        {file.medium && <span className="condensed-badge">Medium</span>}
                        {file.width && file.height && <span className="condensed-badge">Width</span>}
                        {file.width && file.height && <span className="condensed-badge">Height</span>}
                        {file.artwork_title && <span className="condensed-badge">Title</span>}
                      </div>
                    </div>
                    
                    <div className="condensed-status">
                      <div className="status-indicator status-complete">
                        <CheckCircle className="w-3 h-3" />
                        Complete
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="no-artworks">
              <Image className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-center">No artworks uploaded</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Payment Section */}
      <div className="payment-section">
        {feeWaiverStatus === 'active' ? (
          <div className="fee-waiver-notice">
            <div className="waiver-header">
              <CheckCircle className="waiver-icon" />
              <h3 className="waiver-title">Fee Waived</h3>
            </div>
            <p className="waiver-message">
              Your submission fee has been waived. You can submit your application without payment.
            </p>
          </div>
        ) : feeWaiverStatus === 'checking' ? (
          <div className="fee-checking-notice">
            <div className="checking-header">
              <Info className="checking-icon" />
              <h3 className="checking-title">Checking Fee Waiver Status...</h3>
            </div>
          </div>
        ) : (
          <div className="payment-summary">
            <h3 className="payment-title">Payment Summary</h3>
            <div className="fee-breakdown">
              <div className="fee-line">
                <span className="fee-label">Submission Fee</span>
                <span className="fee-amount">${submissionData.submission_price}</span>
              </div>
              <div className="fee-line total">
                <span className="fee-label">Total</span>
                <span className="fee-amount">${submissionData.submission_price}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Terms & Conditions */}
        <div className="terms-section">
          <div className="terms-checkbox">
            <label className="terms-label">
              <input 
                type="checkbox" 
                checked={agreeToTerms}
                onChange={handleTermsChange}
                className="terms-input"
              />
              <span className="terms-text">
                I agree to the terms and conditions and confirm that all information provided is accurate
              </span>
            </label>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="submit-button"
            onClick={onSubmit}
            disabled={!agreeToTerms || files.length === 0 || saving}
          >
            {saving ? (
              <>
                <div className="submission-spinner"></div>
                Submitting...
              </>
            ) : (
              <>
                {feeWaiverStatus === 'active' ? (
                  <>Submit Application (Fee Waived)</>
                ) : (
                  <>Submit Application & Pay ${submissionData.submission_price}</>
                )}
                <CheckCircle className="w-5 h-5 ml-2" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmit;
